/**
 * Centralized deterministic aliases for flexible RFQ Excel import.
 * Matching is exact-after-normalization only (no fuzzy / LLM).
 */

/** Canonical keys used in raw header extraction (before formPatch resolution). */
export type RfqExcelHeaderAliasKey =
  | 'rfqNumber'
  | 'revisionNo'
  | 'currency'
  | 'quoteDeadline'
  | 'quoteValidityDays'
  | 'urgencyLevel'
  | 'requisitionRef'
  | 'vesselName'
  | 'imoNo'
  | 'equipmentTags'
  | 'category'
  | 'subCategory'
  | 'brand'
  | 'model'
  | 'department'
  | 'estimatedTotalCost'
  | 'remarks'
  | 'contactName'
  | 'contactEmail'
  | 'contactPhone'
  | 'contactDepartment'
  | 'deliveryRequiredAt'
  | 'deliveryType'
  | 'portName'
  | 'portNameInput'
  | 'portEtaDate'
  | 'portAgentName'
  | 'portAgentNameInput'
  | 'leadDate'
  | 'creditType'
  | 'paymentTerm'
  | 'insuranceTerm'
  | 'taxTerm'
  | 'transportTerm'
  | 'deliveryTerm'
  | 'packingTerm'
  | 'requiredPaymentTerms'
  | 'requiredCertificates'
  | 'requiredDocuments'
  | 'packingInstructions'
  | 'partialDeliveryAllowed'
  | 'vendors';

export type RfqExcelItemAliasKey =
  | 'itemDescription'
  | 'requiredQuantity'
  | 'uom'
  | 'quality'
  | 'drawingNumber'
  | 'partNo'
  | 'impaNo'
  | 'altPartNo'
  | 'positionNo'
  | 'modelSerial'
  | 'dimensions'
  | 'deliveryPriority'
  | 'generalRemark'
  | 'expectedDeliveryDate';

/**
 * Normalize a header/label for deterministic comparison.
 * Values themselves are never passed through this for semantic stripping —
 * only labels/column titles.
 */
export function normalizeExcelLabel(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_./\\-]+/g, ' ')
    .replace(/[#*]+/g, '')
    .replace(/[^a-z0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Compact form used for alias lookup maps (spaces removed). */
export function compactExcelLabel(value: string): string {
  return normalizeExcelLabel(value).replace(/\s+/g, '');
}

function buildAliasLookup(
  aliases: Record<string, readonly string[]>
): Map<string, string> {
  const map = new Map<string, string>();
  for (const [key, labels] of Object.entries(aliases)) {
    for (const label of labels) {
      const compact = compactExcelLabel(label);
      if (!compact) continue;
      // First registration wins — do not let later aliases steal a key.
      if (!map.has(compact)) {
        map.set(compact, key);
      }
    }
  }
  return map;
}

/** Header field aliases (display labels → canonical raw keys). */
export const RFQ_EXCEL_HEADER_ALIASES: Record<RfqExcelHeaderAliasKey, readonly string[]> = {
  rfqNumber: [
    'RFQ Number',
    'RFQ No',
    'RFQ No.',
    'RFQ #',
    'RFQ Reference',
    'RFQ Ref',
    'RFQ_ID',
    'RFQ ID',
    'Request Number',
    'Reference Number',
  ],
  revisionNo: ['Revision', 'Rev', 'Revision No', 'Rev No', 'Revision Number'],
  currency: ['Currency', 'Currency Code', 'Quote Currency'],
  quoteDeadline: [
    'Quote Deadline',
    'Deadline',
    'Quotation Deadline',
    'Quote Due Date',
    'Due Date',
    'Closing Date',
  ],
  quoteValidityDays: [
    'Validity',
    'Quote Validity',
    'Validity Days',
    'Quotation Validity',
    'Quote Validity Days',
  ],
  urgencyLevel: ['Urgency', 'Urgency Level', 'Priority'],
  requisitionRef: [
    'Requisition Ref',
    'Requisition Reference',
    'Requisition No',
    'Requisition Number',
    'Req Ref',
  ],
  vesselName: ['Vessel Name', 'Vessel', 'Ship Name', 'Ship'],
  imoNo: ['IMO No', 'IMO Number', 'IMO', 'IMO No.'],
  equipmentTags: [
    'Equipment Tags',
    'Equipment',
    'Equipment Tag',
    'Equipment Name',
  ],
  category: ['Category', 'Product Category', 'Equipment Category'],
  subCategory: [
    'Sub Category',
    'Subcategory',
    'Sub-Category',
    'Product Subcategory',
  ],
  brand: ['Brand', 'Manufacturer', 'Make', 'OEM', 'Manufacturer Name'],
  model: ['Model', 'Model No', 'Model Number', 'Equipment Model'],
  department: [
    'Department',
    'Requesting Department',
    'Required Department',
  ],
  estimatedTotalCost: [
    'Estimated Total Cost',
    'Estimated Cost',
    'Estimated Value',
    'Budget',
    'Estimated Budget',
  ],
  remarks: ['Remarks', 'Remark', 'Notes', 'Comments', 'General Remarks'],
  contactName: [
    'Contact Name',
    'Contact Person',
    'Contact',
    'Requestor',
    'Requester',
    'Customer Name',
  ],
  contactEmail: ['Contact Email', 'Email', 'Email Address'],
  contactPhone: [
    'Contact Phone',
    'Phone',
    'Mobile',
    'Phone Number',
    'Contact Number',
  ],
  contactDepartment: ['Contact Department', 'Contact Dept'],
  deliveryRequiredAt: ['Delivery Required At', 'Delivery Location Type'],
  deliveryType: ['Delivery Type'],
  portName: [
    'Port Name',
    'Port',
    'Supply Port',
    'Delivery Port',
    'Delivery Location',
  ],
  portNameInput: [
    'Port Name',
    'Port',
    'Supply Port',
    'Delivery Port',
    'Delivery Location',
  ],
  portEtaDate: ['Port ETA Date', 'Port ETA', 'ETA Date', 'ETA'],
  portAgentName: ['Port Agent Name', 'Port Agent', 'Agent Name'],
  portAgentNameInput: ['Port Agent Name', 'Port Agent', 'Agent Name'],
  leadDate: ['Lead Date', 'Required Lead Date'],
  creditType: ['Credit Type'],
  paymentTerm: ['Payment Term'],
  insuranceTerm: ['Insurance Term'],
  taxTerm: ['Tax Term'],
  transportTerm: ['Transport Term'],
  deliveryTerm: ['Delivery Term', 'Delivery Terms'],
  packingTerm: ['Packing Term'],
  requiredPaymentTerms: ['Payment Terms', 'Required Payment Terms'],
  requiredCertificates: ['Required Certificates', 'Certificates'],
  requiredDocuments: ['Required Documents', 'Documents'],
  packingInstructions: ['Packing Instructions'],
  partialDeliveryAllowed: ['Partial Delivery Allowed'],
  vendors: [
    'Vendors',
    'Vendor',
    'Vendor Name',
    'Vendor Names',
    'Selected Vendors',
  ],
};

export const RFQ_EXCEL_ITEM_ALIASES: Record<RfqExcelItemAliasKey, readonly string[]> = {
  itemDescription: [
    'Description',
    'Item Description',
    'Product Description',
    'Material Description',
    'Quoted Description',
    'Item',
  ],
  requiredQuantity: [
    'Quantity',
    'Qty',
    'Required Quantity',
    'Required Qty',
  ],
  uom: ['UOM', 'Unit', 'Unit of Measure', 'Units'],
  quality: ['Quality'],
  drawingNumber: [
    'Drw',
    'Drawing',
    'Drawing No',
    'Drawing Number',
    'Drawing#',
    'Drawing No.',
  ],
  partNo: [
    'Part No',
    'Part Number',
    'Part#',
    'Part Code',
    'Part No.',
    'Quoted Part Number',
    'Quoted Part No',
  ],
  impaNo: ['IMPA', 'IMPA No', 'IMPA Number', 'IMPA Code', 'IMPA No.'],
  altPartNo: [
    'Alt Part No',
    'Alternate Part No',
    'Alternative Part Number',
    'Alt Part Number',
  ],
  positionNo: [
    'Pos',
    'Position',
    'Position No',
    'Position Number',
    'Position No.',
    'Line No',
    'Line Number',
    'Line #',
  ],
  modelSerial: [
    'Model/Serial',
    'Model Serial',
    'Model / Serial',
    'Serial Number',
    'Serial No',
  ],
  dimensions: ['Dimensions', 'Dim', 'Size'],
  deliveryPriority: ['Priority', 'Delivery Priority'],
  generalRemark: ['Remarks', 'Remark', 'Notes', 'Comments', 'General Remark'],
  expectedDeliveryDate: [
    'Expected Delivery Date',
    'Delivery Date',
    'Item Delivery Date',
    'Required Delivery Date',
  ],
};

const HEADER_LOOKUP = buildAliasLookup(RFQ_EXCEL_HEADER_ALIASES);
const ITEM_LOOKUP = buildAliasLookup(RFQ_EXCEL_ITEM_ALIASES);

/**
 * Ambiguous compact labels that map to multiple conceptual fields.
 * Resolved with context-aware preference (header vs item).
 */
const HEADER_AMBIGUOUS: Record<string, RfqExcelHeaderAliasKey> = {
  // "Priority" in header → urgency; item table uses deliveryPriority via item lookup
  priority: 'urgencyLevel',
  // "Model" alone in header → equipment model (not modelSerial)
  model: 'model',
};

const ITEM_AMBIGUOUS: Record<string, RfqExcelItemAliasKey> = {
  model: 'modelSerial',
  priority: 'deliveryPriority',
  remarks: 'generalRemark',
  remark: 'generalRemark',
  notes: 'generalRemark',
  comments: 'generalRemark',
};

export function resolveHeaderAliasKey(label: string): RfqExcelHeaderAliasKey | null {
  const compact = compactExcelLabel(label);
  if (!compact) return null;
  if (HEADER_AMBIGUOUS[compact]) return HEADER_AMBIGUOUS[compact];
  const hit = HEADER_LOOKUP.get(compact);
  return (hit as RfqExcelHeaderAliasKey | undefined) ?? null;
}

export function resolveItemAliasKey(label: string): RfqExcelItemAliasKey | null {
  const compact = compactExcelLabel(label);
  if (!compact) return null;
  if (ITEM_AMBIGUOUS[compact]) return ITEM_AMBIGUOUS[compact];
  const hit = ITEM_LOOKUP.get(compact);
  return (hit as RfqExcelItemAliasKey | undefined) ?? null;
}

/** Keys that are extracted but not applied to Create RFQ formPatch. */
export const RFQ_EXCEL_NON_FORM_HEADER_KEYS = new Set<string>(['rfqNumber']);

/**
 * Offer/quotation line columns that may appear in item tables but are NOT
 * part of the RFQ item model / create payload. Detected for review only.
 */
export const RFQ_EXCEL_UNSUPPORTED_QUOTATION_ITEM_LABELS: Record<string, readonly string[]> = {
  unitPrice: ['Unit Price', 'UnitPrice', 'Price / Unit', 'Price Per Unit'],
  amount: ['Amount', 'Line Amount', 'Line Total', 'Extended Amount'],
};

const UNSUPPORTED_QUOTATION_LOOKUP = (() => {
  const map = new Map<string, string>();
  for (const [key, labels] of Object.entries(RFQ_EXCEL_UNSUPPORTED_QUOTATION_ITEM_LABELS)) {
    for (const label of labels) {
      const compact = compactExcelLabel(label);
      if (compact && !map.has(compact)) map.set(compact, key);
    }
  }
  return map;
})();

export function resolveUnsupportedQuotationItemLabel(
  label: string
): 'unitPrice' | 'amount' | null {
  const compact = compactExcelLabel(label);
  if (!compact) return null;
  const hit = UNSUPPORTED_QUOTATION_LOOKUP.get(compact);
  return (hit as 'unitPrice' | 'amount' | undefined) ?? null;
}

/** True for empty / placeholder cell values that should not be treated as real data. */
export function isPlaceholderExcelValue(raw: string | null | undefined): boolean {
  const text = String(raw ?? '').trim();
  if (!text) return true;
  const normalized = normalizeExcelLabel(text);
  const compact = compactExcelLabel(text);
  if (!normalized) return true;
  if (normalized === '-' || normalized === '–' || normalized === '—') return true;
  if (compact === '-' || compact === '–' || compact === '—') return true;
  return (
    normalized === 'n/a' ||
    normalized === 'na' ||
    compact === 'na' ||
    compact === 'n/a' ||
    normalized === 'null' ||
    compact === 'null' ||
    normalized === 'none' ||
    compact === 'none' ||
    normalized === 'nil' ||
    compact === 'nil' ||
    normalized === 'blank' ||
    compact === 'blank' ||
    normalized === 'not available' ||
    compact === 'notavailable' ||
    normalized === 'not applicable' ||
    compact === 'notapplicable'
  );
}
